import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { resetPassword } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";
import { Lock, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { BRAND_NAME, BRAND_TAGLINE } from "@/lib/brand";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const email = params.get("email") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error(t("auth.toast_missing_token"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error(t("auth.toast_passwords_mismatch"));
    if (pw.length < 8) return toast.error(t("auth.toast_password_too_short"));
    setLoading(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.toast_reset_failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="reset-password-page">
      <div className="hidden lg:flex flex-col justify-between bg-[#064E3B] text-[#FDFBF7] p-12 relative overflow-hidden">
        <div className="absolute inset-0 pattern-islamic opacity-30" />
        <Link to="/" className="relative flex items-center gap-2.5 z-10">
          <img src="/Hikma-Verse-Logo.png" alt="Hikmah Verses" className="w-9 h-9 rounded-xl object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-semibold text-sm" dir="ltr">{BRAND_NAME}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-70" dir="ltr">{BRAND_TAGLINE}</div>
          </div>
        </Link>
        <div className="relative z-10">
          <div className="font-mushaf-prose text-4xl">إِنَّ مَعَ ٱلْعُسْرِ يُسْرًا</div>
          <div className="mt-6 max-w-md text-[#FDFBF7]/75 text-sm italic">{t("auth.reset_verse_translation")}</div>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-[0.22em] opacity-60">{t("auth.hero_footer")}</div>
      </div>

      <div className="flex flex-col items-center justify-center px-5 sm:px-10 py-12">
        <div className="w-full max-w-[448px]">
          {!done && !token && (
            <div data-testid="reset-invalid" className="rounded-2xl bg-[#FEF2F2] border-2 border-[#EF4444] p-6">
              <AlertTriangle className="w-10 h-10 text-[#EF4444]" />
              <h2 className="font-heading text-2xl font-semibold text-[#1F2937] mt-4">{t("auth.invalid_link_title")}</h2>
              <p className="mt-2 text-sm text-[#4B5563]">{t("auth.invalid_link_body")}</p>
              <Link to="/forgot-password" className="mt-4 inline-block text-[#064E3B] font-medium hover:underline">{t("auth.request_new_link")}</Link>
            </div>
          )}

          {!done && token && (
            <>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1F2937]">{t("auth.reset_title")}</h1>
              {email && <p className="mt-2 text-sm text-[#4B5563]">{t("auth.for_account")} <b>{email}</b></p>}

              <form onSubmit={submit} className="mt-8 space-y-3">
                <Field icon={<Lock className="w-4 h-4" />}>
                  <input data-testid="reset-input-pw1" required type="password" minLength={8} placeholder={t("auth.new_password_placeholder")}
                    value={pw} onChange={(e) => setPw(e.target.value)}
                    className="w-full bg-transparent outline-none text-sm" />
                </Field>
                <Field icon={<Lock className="w-4 h-4" />}>
                  <input data-testid="reset-input-pw2" required type="password" minLength={8} placeholder={t("auth.confirm_password_placeholder")}
                    value={pw2} onChange={(e) => setPw2(e.target.value)}
                    className="w-full bg-transparent outline-none text-sm" />
                </Field>
                <button data-testid="reset-submit-btn" type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium transition disabled:opacity-50">
                  {loading ? t("auth.resetting") : t("auth.reset_password_btn")} {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}

          {done && (
            <div data-testid="reset-success-state" className="rounded-2xl bg-[#ECFDF5] border-2 border-[#10B981] p-6">
              <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
              <h2 className="font-heading text-2xl font-semibold text-[#1F2937] mt-4">{t("auth.reset_done_title")}</h2>
              <p className="mt-2 text-sm text-[#4B5563]">
                {t("auth.reset_done_body")}
              </p>
            </div>
          )}

          <div className="mt-6 text-sm text-[#4B5563] text-center">
            <Link to="/login" className="text-[#064E3B] font-medium hover:underline">{t("auth.back_to_signin")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon, children }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-[#E7E0D2] focus-within:border-[#064E3B] transition">
      <span className="text-[#4B5563]">{icon}</span>
      {children}
    </div>
  );
}
